import type { MainLoop } from '@/infrastructure/engine3d/mainLoop/MainLoop';

type TWebGLContextRecoveryControllerParams = {
    canvasElement: HTMLCanvasElement;
    mainLoop: MainLoop;
    // Must throw on failure so the retry loop can catch it.
    onContextRestored: () => void;
};

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

// Handles mobile WebGL context loss (memory pressure, backgrounding).
// Stops the loop on loss; retries the restore callback up to
// MAX_RETRIES times.
class WebGLContextRecoveryController {
    private readonly _boundContextLoss: (event: Event) => void;
    private readonly _boundContextRestore: () => void;
    private readonly _canvasElement: HTMLCanvasElement;
    private _contextRestoreRetries = 0;
    private readonly _mainLoop: MainLoop;
    private readonly _onContextRestored: () => void;

    constructor(params: TWebGLContextRecoveryControllerParams) {
        this._canvasElement = params.canvasElement;
        this._mainLoop = params.mainLoop;
        this._onContextRestored = params.onContextRestored;

        this._boundContextLoss = (event) => this._handleContextLoss(event);
        this._boundContextRestore = () => this._handleContextRestore();

        this._canvasElement.addEventListener('webglcontextlost', this._boundContextLoss);
        this._canvasElement.addEventListener('webglcontextrestored', this._boundContextRestore);
    }

    dispose(): void {
        this._canvasElement.removeEventListener('webglcontextlost', this._boundContextLoss);
        this._canvasElement.removeEventListener('webglcontextrestored', this._boundContextRestore);
    }

    private _handleContextLoss(event: Event): void {
        event.preventDefault();
        console.warn('WebGL context lost. Attempting to recover...');
        this._mainLoop.stop();
    }

    private _handleContextRestore(): void {
        console.warn('WebGL context restored, restarting main loop...');
        try {
            this._onContextRestored();
            this._mainLoop.start();
            console.warn('WebGL context successfully restored');
        } catch (error) {
            this._contextRestoreRetries++;
            if (this._contextRestoreRetries < MAX_RETRIES) {
                console.warn(`Retrying context restoration (${this._contextRestoreRetries}/${MAX_RETRIES})...`);
                setTimeout(() => this._handleContextRestore(), RETRY_DELAY_MS);
            } else {
                console.error('Failed to restore WebGL context:', error);
            }
        }
    }
}

export { WebGLContextRecoveryController };
